import * as XLSX from "xlsx-js-style";
import { ParticipantStatus } from "../../../utils/getParticipantStatusStyle";

const ExportInsuranceButton = {
  export: ({ participants, filter }) => {
    if (!["nnwChronic", "nnwNoChronic", "krChronic", "krNoChronic"].includes(filter)) {
      return;
    }

    const applyStyle = (worksheet, cell, style) => {
      if (!worksheet[cell]) return;
      worksheet[cell].s = style;
    };

    const getFileName = () => {
      switch (filter) {
        case "nnwChronic": return "Wniosek o ubezpieczenie NNW - przewlekle.xlsx";
        case "nnwNoChronic": return "Wniosek o ubezpieczenie NNW - nieprzewlekle.xlsx";
        case "krChronic": return "Wniosek o ubezpieczenie KR - przewlekle.xlsx";
        case "krNoChronic": return "Wniosek o ubezpieczenie KR - nieprzewlekle.xlsx";
        default: return "Wniosek.xlsx";
      }
    };

    const now = new Date();
    const formattedDateTime = now.toLocaleString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

    const headerTitle = `Wygenerowano: ${formattedDateTime}`;
    const header = [[headerTitle], []];

    const isNNW = filter.startsWith("nnw");

    const isCovered = (p) =>
      (filter === "nnwChronic" && p.nnwCovered && p.isChronicIllness) ||
      (filter === "nnwNoChronic" && p.nnwCovered && !p.isChronicIllness) ||
      (filter === "krChronic" && p.krCovered && p.isChronicIllness) ||
      (filter === "krNoChronic" && p.krCovered && !p.isChronicIllness);

    const isUncovered = (p) =>
      (filter === "nnwChronic" && !p.nnwCovered && p.isChronicIllness) ||
      (filter === "nnwNoChronic" && !p.nnwCovered && !p.isChronicIllness) ||
      (filter === "krChronic" && !p.krCovered && p.isChronicIllness) ||
      (filter === "krNoChronic" && !p.krCovered && !p.isChronicIllness);

    const active = participants.filter(p => p.status !== ParticipantStatus.Cancelled);
    const cancelled = participants.filter(p => p.status === ParticipantStatus.Cancelled);

    const filteredUncovered = active.filter(isUncovered);
    const filteredCovered = active.filter(isCovered);
    const cancelledUncovered = cancelled.filter(isUncovered);
    const cancelledCovered = cancelled.filter(isCovered);

    const formatDateCell = (dateStr) => ({
      v: dateStr ? new Date(dateStr) : "",
      t: "d",
      z: "yyyy-mm-dd"
    });

    const mapParticipant = (p, i) => {
      const insuranceCost = isNNW ? p.nnwCost : p.krCost;
      return [
        i + 1,
        p.firstName ?? "Jan",
        p.lastName ?? "Kowalski",
        formatDateCell(p.birthDate),
        p.destinationCountry ?? "-",
        formatDateCell(p.departureDate),
        formatDateCell(p.returnDate),
        formatDateCell(p.paidAt),
        p.tripPrice ?? "-",
        insuranceCost ?? "-"
      ];
    };

    const headers = [
      "Lp.",
      "Imię",
      "Nazwisko",
      "Data urodzenia",
      "Kraj wyjazdu",
      "Data wyjazdu",
      "Data powrotu",
      "Data wpłaty zaliczki",
      "Cena imprezy",
      "Cena ubezpieczenia"
    ];

    const sectionUncovered = [["NIEWYKUPIONE"], headers, ...filteredUncovered.map(mapParticipant)];
    const spacer = [[], []];
    const sectionCovered = [["WYKUPIONE"], headers, ...filteredCovered.map(mapParticipant)];
    const sectionCancelledUncovered = [["NIEWYKUPIONE ANULOWANE"], headers, ...cancelledUncovered.map(mapParticipant)];
    const sectionCancelledCovered = [["WYKUPIONE ANULOWANE"], headers, ...cancelledCovered.map(mapParticipant)];

    const fullSheetData = [
      ...header,
      ...sectionUncovered,
      ...spacer,
      ...sectionCovered,
      ...spacer,
      ...sectionCancelledUncovered,
      ...spacer,
      ...sectionCancelledCovered,
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(fullSheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Wniosek");

    // 🔹 Style sekcji
    const redHeaderStyle = {
      fill: { fgColor: { rgb: "FFFFCCCC" } },
      font: { bold: true, color: { rgb: "FF0000" } },
    };
    const greenHeaderStyle = {
      fill: { fgColor: { rgb: "FFCCFFCC" } },
      font: { bold: true, color: { rgb: "006600" } },
    };
    const greyHeaderStyle = {
      fill: { fgColor: { rgb: "FFEFEFEF" } },
      font: { bold: true, color: { rgb: "888888" } },
    };

    const insuranceCostStyle = {
      font: { bold: true, color: { rgb: "000000" } }
    };

    const allHeaderRowIndexes = fullSheetData
      .map((r, i) => (r && r[0] === "Lp.") ? i : null)
      .filter(i => i !== null);

    allHeaderRowIndexes.forEach(rowIndex => {
      const cell = XLSX.utils.encode_cell({ c: 9, r: rowIndex });
      applyStyle(worksheet, cell, insuranceCostStyle);
    });

    const dataRowIndexes = fullSheetData
      .map((r, i) => (r && typeof r[0] === "number") ? i : null)
      .filter(i => i !== null);

    dataRowIndexes.forEach(rowIndex => {
      const cell = XLSX.utils.encode_cell({ c: 9, r: rowIndex });
      applyStyle(worksheet, cell, insuranceCostStyle);
    });

    const redHeaderCell = "A" + (header.length + 1);
    const greenHeaderCell = "A" + (header.length + sectionUncovered.length + spacer.length + 1);
    const cancelledUncoveredCell = XLSX.utils.encode_cell({
      c: 0,
      r: fullSheetData.findIndex(r => r[0] === "NIEWYKUPIONE ANULOWANE"),
    });
    const cancelledCoveredCell = XLSX.utils.encode_cell({
      c: 0,
      r: fullSheetData.findIndex(r => r[0] === "WYKUPIONE ANULOWANE"),
    });

    applyStyle(worksheet, redHeaderCell, redHeaderStyle);
    applyStyle(worksheet, greenHeaderCell, greenHeaderStyle);
    applyStyle(worksheet, cancelledUncoveredCell, greyHeaderStyle);
    applyStyle(worksheet, cancelledCoveredCell, greyHeaderStyle);

    const dateColumns = [3, 5, 6, 7]; 
    const colWidths = [];

    fullSheetData.forEach(row => {
      row.forEach((cell, colIdx) => {
        const value = typeof cell === "object" && cell.v !== undefined ? cell.v : cell;
        const str = value ? value.toString() : "";
        const length = str.length;
        colWidths[colIdx] = Math.max(colWidths[colIdx] || 10, length + 3);
      });
    });

    worksheet["!cols"] = colWidths.map((wch, i) => {
      if (i === 0) return { wch: 5 }; 
      if (dateColumns.includes(i)) return { wch: 14 }; 
      return { wch: Math.min(Math.max(12, wch), 40) }; 
    });

    XLSX.writeFile(workbook, getFileName());
  }
};

export default ExportInsuranceButton;
